/**
 * c3controls configurator — storefront web component.
 *
 * C3 products are build-to-order catalog numbers (up to ~10^15 combinations)
 * that cannot be Shopify variants. Each product line is one Shopify product
 * with a single min-price placeholder variant; this element loads the line's
 * JSON schema (vendored as a theme asset) plus the rule engine
 * (`c3-validator.js`, exact port of c3controls.com's ConfigFilters) and lets
 * the buyer build a valid catalog number with live pricing.
 *
 * On add-to-cart the selection is attached as line item properties (`_cfg` is
 * the checkout authority — the draft-order backend re-derives the price from
 * it; `_unitPrice` is display-only). See tooling/c3controls/IMPLEMENTATION.md.
 *
 * Helper functions below are plain-JS ports of tooling/c3controls/index.ts.
 * Keep them in sync with that file.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { addConfiguredToBom, markBomButtonAdded, openBomDrawer, playBomAddedAnimation } from '@theme/bom-add';

/* ------------------------------------------------------------------ */
/* Ports of tooling/c3controls/index.ts helpers                        */
/* ------------------------------------------------------------------ */

/**
 * Whether an option can be picked right now. Unlike `filterChoices`'s
 * `excluded` flag, this only considers the option's own rules and its table's
 * rules — NOT conflicts with other current selections. Cross-conflicts are
 * resolved in the pick's favor by `applyPick` (the source configurator
 * behaves the same way: switching e.g. Relay Type clears dependent choices
 * instead of locking the switch).
 */
function optionAvailable(validator, schema, selection, table, option) {
  const optIds = new Set(
    Object.values(selection).filter((id) => id !== selection[table.id]),
  );
  optIds.add(option.id);
  const tblIds = new Set([...Object.keys(selection), table.id]);
  return (
    validator.itemSurvives(option.rules, optIds, tblIds, schema) &&
    validator.itemSurvives(table.rules, optIds, tblIds, schema)
  );
}

/**
 * Apply a pick and drop every other selection the rules now exclude (e.g.
 * picking a new operator can remove whole tables). The fresh pick wins
 * conflicts. Repeats to a fixpoint because removing one selection can
 * cascade-exclude others.
 */
function applyPick(validator, schema, selection, tableId, optionId) {
  let next = { ...selection, [tableId]: optionId };
  for (let i = 0; i < 20; i++) {
    const choices = validator.filterChoices(schema, next);
    const pruned = Object.fromEntries(
      Object.entries(next).filter(([tid, oid]) => {
        if (tid === tableId) return true; // the fresh pick wins conflicts
        const table = choices.tables.find((t) => t.id === tid);
        if (!table || table.excluded) return false;
        const option = table.options.find((o) => o.id === oid);
        return Boolean(option && !option.excluded);
      }),
    );
    if (Object.keys(pruned).length === Object.keys(next).length) break;
    next = pruned;
  }
  // Safety net: if the pick is still excluded in the settled context
  // (pathological rule cycles), drop it rather than keep an invalid selection.
  const settled = validator.filterChoices(schema, next);
  const pickedTable = settled.tables.find((t) => t.id === tableId);
  const pickedOption = pickedTable?.options.find((o) => o.id === optionId);
  if (!pickedTable || pickedTable.excluded || !pickedOption || pickedOption.excluded) {
    const { [tableId]: _dropped, ...rest } = next;
    return rest;
  }
  return next;
}

/** Human-readable one-line summary, e.g. "Control Relay, Miniature, 4 Pole, 24V AC (50/60Hz)". */
function configurationSummary(schema, selection) {
  const labels = [];
  for (const table of schema.tables) {
    const optionId = selection[table.id];
    if (!optionId) continue;
    const option = table.options.find((o) => o.id === optionId);
    if (option) labels.push(option.label);
  }
  return labels.join(', ');
}

/**
 * Compact, unambiguous selection encoding: `tableIndex.optionIndex` pairs
 * joined by commas (indices into schema.tables / table.options). Carries the
 * selection through Shopify line-item properties so the server can re-derive
 * (and re-price) the exact configuration at checkout.
 */
function encodeSelection(schema, selection) {
  const parts = [];
  schema.tables.forEach((table, tableIndex) => {
    const optionId = selection[table.id];
    if (!optionId) return;
    const optionIndex = table.options.findIndex((o) => o.id === optionId);
    if (optionIndex >= 0) parts.push(`${tableIndex}.${optionIndex}`);
  });
  return parts.join(',');
}

const ATTR_CATALOG_NUMBER = '_catalogNumber';
const ATTR_CONFIGURATION = '_configuration';
const ATTR_UNIT_PRICE = '_unitPrice';
const ATTR_SELECTION = '_cfg';

/** Shopify caps line-item property values; split long values into key, key2, key3… chunks. */
const ATTR_CHUNK_SIZE = 250;

function chunkAttribute(key, value) {
  if (value.length <= ATTR_CHUNK_SIZE) return [{ key, value }];
  const attributes = [];
  for (let i = 0, chunk = 1; i < value.length; i += ATTR_CHUNK_SIZE, chunk++) {
    attributes.push({ key: chunk === 1 ? key : `${key}${chunk}`, value: value.slice(i, i + ATTR_CHUNK_SIZE) });
  }
  return attributes;
}

/** Build the full property set for a validated selection. */
function buildCartAttributes(validator, schema, selection) {
  const partNumber = validator.buildPartNumber(schema, selection);
  const price = validator.priceOf(schema, selection);
  return [
    { key: ATTR_CATALOG_NUMBER, value: partNumber },
    ...chunkAttribute(ATTR_CONFIGURATION, configurationSummary(schema, selection)),
    { key: ATTR_UNIT_PRICE, value: price.toFixed(2) },
    ...chunkAttribute(ATTR_SELECTION, encodeSelection(schema, selection)),
  ];
}

/* ------------------------------------------------------------------ */
/* <c3-configurator>                                                   */
/* ------------------------------------------------------------------ */

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

class C3Configurator extends HTMLElement {
  connectedCallback() {
    this.#init().catch((error) => {
      console.error('[c3-configurator] failed to load:', error);
      this.innerHTML =
        '<p class="c3-configurator__error" role="alert">This product configurator could not be loaded. Refresh the page and try again.</p>';
    });
  }

  async #init() {
    const schemaUrl = this.dataset.schemaUrl;
    const validatorUrl = this.dataset.validatorUrl;
    if (!schemaUrl || !validatorUrl || !this.dataset.variantId) {
      throw new Error('missing schema-url / validator-url / variant-id');
    }

    const [schemaResponse, validator] = await Promise.all([
      fetch(schemaUrl),
      import(/* webpackIgnore: true */ validatorUrl),
    ]);
    if (!schemaResponse.ok) throw new Error(`schema fetch failed (${schemaResponse.status})`);

    this.validator = validator;
    this.schema = await schemaResponse.json();
    this.selection = { ...this.schema.meta.minPriceSelection };

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);
    this.#render();
    this.setAttribute('aria-busy', 'false');
    this.dispatchEvent(new CustomEvent('c3-configurator:ready', { bubbles: true }));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
  }

  #handleChange = (event) => {
    const select = event.target.closest?.('select[data-table-id]');
    if (select) {
      const tableId = select.dataset.tableId;
      if (select.value) {
        this.selection = applyPick(this.validator, this.schema, this.selection, tableId, select.value);
      } else {
        const { [tableId]: _dropped, ...rest } = this.selection;
        this.selection = rest;
      }
      this.#render({ restoreTableId: tableId });
      return;
    }
    if (event.target.matches?.('input[data-c3-quantity]')) {
      const qty = Math.max(1, Math.floor(Number(event.target.value) || 1));
      event.target.value = String(qty);
      this.quantity = qty;
    }
  };

  #handleClick = (event) => {
    if (event.target.closest?.('[data-c3-add]')) {
      this.#addToCart(event.target.closest('[data-c3-add]'));
      return;
    }
    if (event.target.closest?.('[data-c3-bom]')) {
      const button = event.target.closest('[data-c3-bom]');
      if (button.dataset.bomState === 'added') {
        openBomDrawer();
        return;
      }
      this.#addToBom(button);
    }
  };

  applyPartNumber(partNumber) {
    if (!partNumber || !this.validator || !this.schema) return false;
    const next = this.validator.selectionFromPartNumber(this.schema, partNumber);
    if (!next) return false;
    const result = this.validator.validateSelection(this.schema, next);
    if (!result.valid) return false;
    this.selection = next;
    this.#render();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    this.querySelector('select, button, input')?.focus({ preventScroll: true });
    return true;
  }

  quantity = 1;

  #render({ restoreTableId } = {}) {
    const { schema, selection, validator } = this;
    const choices = validator.filterChoices(schema, selection);
    const result = validator.validateSelection(schema, selection);
    const partNumber = validator.buildPartNumber(schema, selection);
    const price = validator.priceOf(schema, selection);
    const statusId = `c3-status-${this.dataset.handle}`;
    const hintId = `c3-hint-${this.dataset.handle}`;

    const fields = choices.tables
      .filter((table) => !table.excluded)
      .map((table) => {
        const schemaTable = schema.tables.find((t) => t.id === table.id);
        const current = selection[table.id];
        const available = schemaTable.options.filter((option) =>
          optionAvailable(validator, schema, selection, schemaTable, option),
        );
        const options = available
          .map((schemaOption) => {
            const delta =
              schemaOption.price !== 0
                ? ` (${schemaOption.price > 0 ? '+' : '−'}${money.format(Math.abs(schemaOption.price))})`
                : '';
            const selected = current === schemaOption.id ? ' selected' : '';
            return `<option value="${escapeHtml(schemaOption.id)}"${selected}>${escapeHtml(schemaOption.label)}${delta}</option>`;
          })
          .join('');
        const placeholder = current
          ? ''
          : `<option value="" selected disabled>Select ${escapeHtml(schemaTable.label)}…</option>`;
        return `
          <div class="c3-configurator__field">
            <label class="c3-configurator__label" for="c3-${this.dataset.handle}-${escapeHtml(table.id)}">
              ${escapeHtml(schemaTable.label)}
            </label>
            <select
              class="c3-configurator__select"
              id="c3-${this.dataset.handle}-${escapeHtml(table.id)}"
              data-table-id="${escapeHtml(table.id)}"
              data-empty="${current ? 'false' : 'true'}"
            >${placeholder}${options}</select>
          </div>`;
      })
      .join('');

    const subtitleText = schema.subtitle || schema.meta?.subtitle;
    const subtitle = subtitleText
      ? `<p class="c3-configurator__subtitle">${escapeHtml(subtitleText)}</p>`
      : '';
    const labelPartNumber = this.dataset.labelPartNumber || 'Part number';
    const labelPrice = this.dataset.labelPrice || 'Configured price';
    const labelQty = this.dataset.labelQty || 'Quantity';
    const labelAddCart = this.dataset.labelAddCart || 'Add to cart';
    const labelAddBom = this.dataset.labelAddBom || 'Add to BoM';
    const labelHint = this.dataset.labelHint || 'Complete all options to add this product to your cart or BoM.';

    this.innerHTML = `
      <div class="c3-configurator__layout">
        ${subtitle}
        <div class="c3-configurator__options">
          <div class="c3-configurator__fields">${fields}</div>
        </div>
        <div class="c3-configurator__result">
          <div
            class="c3-configurator__summary"
            id="${statusId}"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <div class="c3-configurator__part-number">
              <span class="c3-configurator__part-number-label">${escapeHtml(labelPartNumber)}</span>
              <span class="c3-configurator__part-number-value">${escapeHtml(partNumber || '—')}</span>
            </div>
            <div class="c3-configurator__price-wrap">
              <span class="c3-configurator__price-label">${escapeHtml(labelPrice)}</span>
              <span class="c3-configurator__price">${money.format(price)}</span>
            </div>
          </div>
          <div class="c3-configurator__actions">
            <label class="c3-configurator__qty-label" for="c3-qty-${this.dataset.handle}">
              ${escapeHtml(labelQty)}
            </label>
            <input
              class="c3-configurator__qty"
              id="c3-qty-${this.dataset.handle}"
              type="number"
              inputmode="numeric"
              min="1"
              step="1"
              value="${this.quantity}"
              data-c3-quantity
            >
            <button
              type="button"
              class="button c3-configurator__add"
              data-c3-add
              ${result.valid ? '' : 'disabled'}
              ${result.valid ? '' : `aria-describedby="${hintId}"`}
            >
              ${escapeHtml(labelAddCart)}
            </button>
            <button
              type="button"
              class="button-secondary c3-configurator__bom bom-add__button"
              data-c3-bom
              ${result.valid ? '' : 'disabled'}
              ${result.valid ? '' : `aria-describedby="${hintId}"`}
            >
              <span class="bom-add__label" data-bom-label>${escapeHtml(labelAddBom)}</span>
              ${this.#bomBurstMarkup()}
            </button>
          </div>
          <p class="c3-configurator__bom-status" role="status" aria-live="polite" hidden></p>
          <p class="c3-configurator__error" role="alert" hidden></p>
          ${
            result.valid
              ? ''
              : `<p class="c3-configurator__hint" id="${hintId}">${escapeHtml(labelHint)}</p>`
          }
        </div>
      </div>
    `;

    if (restoreTableId) {
      this.querySelector(`select[data-table-id="${CSS.escape(restoreTableId)}"]`)?.focus();
    }

    this.dispatchEvent(
      new CustomEvent('c3-configurator:change', {
        bubbles: true,
        detail: { partNumber },
      }),
    );
  }

  async #addToCart(button) {
    const { schema, selection, validator } = this;
    const result = validator.validateSelection(schema, selection);
    if (!result.valid) return;

    const errorEl = this.querySelector('.c3-configurator__error');
    const bomStatus = this.querySelector('.c3-configurator__bom-status');
    if (errorEl) errorEl.hidden = true;
    if (bomStatus) bomStatus.hidden = true;
    button.disabled = true;

    const properties = {};
    for (const { key, value } of buildCartAttributes(validator, schema, selection)) {
      properties[key] = value;
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const variantId = String(this.dataset.variantId);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: this.quantity }],
        promise: deferred.promise,
      }),
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: this.quantity, properties }],
          sections: sectionIds,
        }),
      });
      const addBody = await addResponse.json();
      if (!addResponse.ok || addBody.status) {
        throw new Error(addBody.description || addBody.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const ajaxCart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'c3-configurator',
          itemCount: this.quantity,
          sections: addBody.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[c3-configurator] add to cart failed:', error);
      deferred.resolve({ detail: { didError: true, source: 'c3-configurator' } });
      if (errorEl) {
        errorEl.textContent = error.message || 'Could not add to cart. Please try again.';
        errorEl.hidden = false;
      }
      button.disabled = false;
    }
  }

  #bomBurstMarkup() {
    const template = this.closest('.c3-configurator-block')?.querySelector('template[data-c3-bom-burst]');
    return template?.innerHTML ?? '';
  }

  async #addToBom(button) {
    const { schema, selection, validator } = this;
    const result = validator.validateSelection(schema, selection);
    if (!result.valid) return;

    const errorEl = this.querySelector('.c3-configurator__error');
    const bomStatus = this.querySelector('.c3-configurator__bom-status');
    if (errorEl) errorEl.hidden = true;
    if (bomStatus) {
      bomStatus.hidden = true;
      bomStatus.textContent = '';
    }

    button.disabled = true;
    try {
      const partNumber = result.partNumber || validator.buildPartNumber(schema, selection);
      const cfg = encodeSelection(schema, selection);
      const configuration = configurationSummary(schema, selection);
      const unitPrice = Number(result.price ?? validator.priceOf(schema, selection) ?? 0);

      const added = await addConfiguredToBom({
        catalogNumber: partNumber,
        cfg,
        configuration,
        unitPrice,
        quantity: this.quantity,
        variantId: this.dataset.variantId,
        productHandle: this.dataset.handle || '',
        productTitle: this.dataset.productTitle || document.title,
        productId: this.dataset.productId ? Number(this.dataset.productId) : null,
        vendor: this.dataset.vendor || '',
      });

      if (!added) throw new Error('Could not add to BoM.');

      const addedLabel = this.dataset.labelAddedBom || 'Added to BoM';
      if (bomStatus) {
        bomStatus.textContent = addedLabel;
        bomStatus.hidden = false;
      }
      markBomButtonAdded(button, addedLabel);
      playBomAddedAnimation(button);
    } catch (error) {
      console.error('[c3-configurator] add to BoM failed:', error);
      if (errorEl) {
        errorEl.textContent = error.message || 'Could not add to BoM. Please try again.';
        errorEl.hidden = false;
      }
    } finally {
      button.disabled = false;
    }
  }
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

if (!customElements.get('c3-configurator')) {
  customElements.define('c3-configurator', C3Configurator);
}
